import {
  BODY_TYPE_OPTIONS,
  EDUCATION_OPTIONS,
  INTERESTS_OPTIONS,
  MALE_USER_EXCLUDE_BODY_TYPES,
} from '../../components/join/constants';
import { EVENT_NAMES, MIXPANEL_DEFAULT_STATE } from '../../constants/mixpanel';
import store from '../../store';
import { mapIdentifierValues } from '../helpers';

export interface SearchViewEventProperties {
  Source: string;
  'Page Number': number;
  'Filter Applied': boolean;
  Parameters: string[];
  'Matches Number': number;
  'Sorting Mode': string;
  'User Location': string;
}

export interface SearchMixpanelDataProps {
  Source: string;
  Parameters: string[];
  'Filter Applied': boolean;
}

export const mapSearchViewEventProperties = (
  formData: Record<string, any>,
  mixpanelData: SearchMixpanelDataProps,
  totalCount?: number
): SearchViewEventProperties => {
  const defaultState = MIXPANEL_DEFAULT_STATE[EVENT_NAMES.SEARCH_VIEW];

  const sortingModes = {
    recent: 'Recent Activity',
    newest: 'Newest Members',
    distance: 'Nearest To Me',
  };

  const additionalParamsField = {
    Location: ['city', 'country', 'region'],
    'Looking For': [
      'dating_just_dating',
      'dating_friendship',
      'dating_marriage',
      'dating_sugar',
      'dating_married',
      'dating_adult',
      'dating_mentorship',
    ],
    'Profile Keywords': ['keywords'],
    'ID Verified': ['id_verified'],
    Smoking: ['smoking'],
    Drinking: ['drinking'],
    Children: ['children'],
    'Body Types': ['body_types'],
    Education: ['education'],
  };

  const fields = Object.keys(additionalParamsField)
    .map(key => {
      return additionalParamsField[key].some(property => {
        return (
          (typeof formData[property] === 'string' && formData[property] !== '') ||
          (Array.isArray(formData[property]) && formData[property].length > 0) ||
          (typeof formData[property] === 'boolean' && formData[property])
        );
      })
        ? key
        : '';
    })
    .filter(item => item !== '');

  const userLocation = store.getState().profile?.profile?.data?.city ?? '';
  const pageNumber = store.getState().meta?.search_current_page ?? 1;

  const payload: SearchViewEventProperties | any = {
    ...defaultState,
    Source: mixpanelData.Source,
    'Page Number': pageNumber,
    'Matches Number': totalCount ?? 0,
    'Sorting Mode': sortingModes[formData.order],
    'User Location': userLocation,
    Parameters: [...defaultState['Parameters'], ...fields],
  };

  if (formData.distance && formData.distance !== undefined && formData.distance !== 50) {
    payload['Distance'] =
      formData.distance.toString() === '20000' ? 'within any distance' : `${formData.distance} miles away`;
    payload['Filter Applied'] = true;
  }

  if (formData.age_min !== 18 || ![40, 55].includes(formData.age_max)) {
    payload['Age From'] = formData.age_min;
    payload['Age To'] = formData.age_max;
    payload['Filter Applied'] = true;
  }

  if (formData.height_min !== 150 || formData.height_max !== 215) {
    payload['Height From'] = formData.height_min;
    payload['Height To'] = formData.height_max;
    payload['Filter Applied'] = true;
  }

  if (fields.includes('Location')) {
    payload['Location'] = additionalParamsField['Location'].map(key => formData[key]).join(', ');
    payload['Filter Applied'] = true;
  }

  if (fields.includes('Looking For')) {
    payload['Looking For'] = additionalParamsField['Looking For']
      .map(item => {
        const key = formData[item];

        return key && INTERESTS_OPTIONS.find(option => option.value === item)?.label;
      })
      .filter(item => item);
    payload['Filter Applied'] = true;
  }

  if (fields.includes('Body Types')) {
    payload['Body Types'] = formData.body_types
      .map((item: number) => {
        return BODY_TYPE_OPTIONS.find(option => option.value === item)?.label;
      })
      .filter((item: number) => !MALE_USER_EXCLUDE_BODY_TYPES.includes(item));
    payload['Filter Applied'] = true;
  }

  if (fields.includes('Education')) {
    payload['Education'] = formData.education
      .map((item: number) => {
        return EDUCATION_OPTIONS.find(option => option.id === item)?.value;
      })
      .filter((item: number) => item);
    payload['Filter Applied'] = true;
  }

  if (fields.includes('ID Verified')) {
    payload['ID Verified'] = mapIdentifierValues(formData.id_verified);
    payload['Filter Applied'] = true;
  }

  if (fields.includes('Selfie Verified')) {
    payload['Selfie Verified'] = mapIdentifierValues(formData.id_verified);
    payload['Filter Applied'] = true;
  }

  if (fields.includes('Smoking')) {
    payload['Smoking'] = mapIdentifierValues(formData.smoking);
    payload['Filter Applied'] = true;
  }

  if (fields.includes('Children')) {
    payload['Children'] = mapIdentifierValues(formData.children);
    payload['Filter Applied'] = true;
  }

  if (fields.includes('Drinking')) {
    payload['Drinking'] = mapIdentifierValues(formData.drinking);
    payload['Filter Applied'] = true;
  }

  if (fields.includes('Profile Keywords')) {
    payload['Profile Keywords'] = formData.keywords;
    payload['Filter Applied'] = true;
  }

  return payload;
};
